import { Metafields, Product, ProductTypes } from '@/types/ecommerce.types';
import {
  getCurrentVariant,
  getMetafieldV2,
  handleizeProductType,
  isCanvasProduct,
} from '@/utils/utils';

const getProductDescription = (product: Product, shopMetafields?: Metafields) => {
  if (!product) {
    return {};
  }

  const currentVariant = getCurrentVariant(product.variants?.edges);
  const productType = handleizeProductType(product.productType);
  const galleryWidth = getMetafieldV2('gallery_width_in', product?.metafields);
  const galleryHeight = getMetafieldV2('gallery_height_in', product?.metafields);
  const galleryWidthIn = galleryWidth ? Math.round(Number(galleryWidth)) : undefined;
  const galleryHeightIn = galleryHeight ? Math.round(Number(galleryHeight)) : undefined;

  const getShopProductLabel = () => {
    const label = JSON.parse(getMetafieldV2('pdp_product_type_alias', shopMetafields) ?? '{}');
    return label[productType ?? ''];
  };

  const getProductColor = () => currentVariant?.selectedOptions[2]?.value;

  const getProductSize = () => {
    switch (productType) {
      case ProductTypes.GALLERY:
        return galleryWidthIn && galleryHeightIn ? `${galleryWidthIn}" x ${galleryHeightIn}"` : '';
      case ProductTypes.DESIGNSERVICE:
        return '';
      case ProductTypes.MUG:
      case ProductTypes.ORNAMENT:
      case ProductTypes.PUZZLE:
      case ProductTypes.POUCH:
      case ProductTypes.STEELBOTTLE:
        return currentVariant?.selectedOptions[0]?.value;
      default:
        return currentVariant?.selectedOptions[0]?.value + ' frame';
    }
  };

  const getProductPrintSize = () => {
    const printSize = getMetafieldV2('print_size', currentVariant?.metafields);

    switch (true) {
      case productType === ProductTypes.GALLERY:
      case productType === ProductTypes.DESIGNSERVICE:
        return '';
      case productType === ProductTypes.PUZZLE:
        return currentVariant?.selectedOptions[1]?.value + ' size';
      case productType === ProductTypes.POUCH:
        return currentVariant?.selectedOptions[0]?.value + ' size';
      case isCanvasProduct(productType) &&
        productType !== ProductTypes.PHOTOTILE &&
        productType !== ProductTypes.PHOTOTILEWRAP:
        return printSize + ' print';
      default:
        return currentVariant?.selectedOptions[1]?.value + ' print';
    }
  };

  const getProductMount = () => getMetafieldV2('use_type', currentVariant?.metafields);

  const getProductConfiguration = () => {
    switch (productType) {
      case ProductTypes.GALLERY:
        return currentVariant?.selectedOptions[1]?.value;
      case ProductTypes.MUG:
      case ProductTypes.ORNAMENT:
      case ProductTypes.PUZZLE:
      case ProductTypes.STEELBOTTLE:
        return currentVariant?.selectedOptions[0]?.value;
      case ProductTypes.DESIGNSERVICE:
        return currentVariant?.selectedOptions[0]?.value;
      default:
        return '';
    }
  };

  const getDescription = () => {
    switch (productType) {
      case ProductTypes.GALLERY:
        return currentVariant?.selectedOptions[0].value?.toLowerCase();
      default:
        return getShopProductLabel() || '';
    }
  };

  return {
    mount: getProductMount(),
    color: getProductColor(),
    printSize: getProductPrintSize(),
    productSize: getProductSize(),
    description: getDescription(),
    configuration: getProductConfiguration(),
  };
};

export default getProductDescription;
